import { Injectable } from '@angular/core';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { BehaviorSubject, Subscription, fromEvent, merge, startWith, switchMap, timer } from 'rxjs';
import { GA_EVENTS, GA_PROPS } from '../const.variables';
import { getWindow } from 'ssr-window';
import { environment } from 'src/environments/environment';
import { PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ApiService } from './api.service';
import { AuthService } from '../auth/auth.service';

@Injectable({ providedIn: 'root' })
export class HelperService {

  blockScroll$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  hideHeaderFooter$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private initialLoadTime;
  private initialLoadSub: Subscription;
  private windowFocused = true;
  private reloadInterval = null;
  private reloadIntervalSub: Subscription;

  constructor(
    private apiService: ApiService,
    private gaService: GoogleAnalyticsService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
  }

  initHelpers() {
    if (environment.production && !getWindow()['dmode']) {
      console.log = () => {};
    }
    if (this.isBrowser()) {
      this.blockScroll$.subscribe(val => {
        if (val) {
          const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
          document.body.style.overflow = 'hidden';
          if (scrollBarWidth > 0) {
            document.body.style.paddingRight = `${scrollBarWidth}px`;
          }
        } else {
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
        }
      });
      this.windowFocusHandler();
      this.mouseMoveHandler();
    }
  }

  isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  loadWatcherInit() {
    this.initialLoadSub = timer(0, 1).subscribe(time => this.initialLoadTime = time);
  }

  checkVersion() {
    if (this.isBrowser()) {
      this.apiService.getVersion().subscribe(version => {
        if (version) {
          const currentVersion = localStorage.getItem('version');
          if (currentVersion !== version) {
            localStorage.setItem('version', version);
          }
        }
      })
    }
  }

  loadWatcherUserReady(isTalent, unsubscribe = false) {
    const eventName = isTalent ? GA_EVENTS.LoadUserTalentReady : GA_EVENTS.LoadUserHmReady;
    this.gaService.gtag('event', eventName, { 
      [GA_PROPS.LoadTime]: (this.initialLoadTime / 1000).toFixed(2),
      [GA_PROPS.LoadTimeDim]: (this.initialLoadTime / 1000).toFixed(2)
    });
    if (unsubscribe) {
      this.initialLoadSub.unsubscribe();
    }
  }

  loadWatcherDashboardReady(isTalent) {
    this.initialLoadSub.unsubscribe();
    const eventName = isTalent ? GA_EVENTS.LoadUserTalentReadyDashboard : GA_EVENTS.LoadUserHmReadyDashboard;
    this.gaService.gtag('event', eventName, { 
      [GA_PROPS.LoadTime]: (this.initialLoadTime / 1000).toFixed(2),
      [GA_PROPS.LoadTimeDim]: (this.initialLoadTime / 1000).toFixed(2)
    });
    if (this.initialLoadSub) {
      this.initialLoadSub.unsubscribe();
    }
  }

  pushWithDelay(targetArray: any[], sourceArray: any[], delay = 50) {
    let index = 0;
    function pushItem() {
      if (index < sourceArray.length) {
        targetArray.push(sourceArray[index]);
        index++;
        setTimeout(pushItem, delay);
      }
    }
    pushItem();
  }

  private windowFocusHandler() {
    if (document) {
      document.addEventListener('visibilitychange', () => {
        this.windowFocused = document.visibilityState === 'visible';
        if (this.windowFocused && this.reloadIntervalSub) {
          if (this.reloadInterval && this.reloadInterval > 60 * 60) { /// 1 hour
            location.reload();
          }
          this.reloadIntervalSub.unsubscribe();
          this.reloadIntervalSub = null;
          this.reloadInterval = null;
        } else {
          this.reloadIntervalSub = timer(0, 1).subscribe(time => this.reloadInterval = time);
        }
      });
    }
  }

  private mouseMoveHandler() {
    const mouseMove$ = fromEvent<MouseEvent>(getWindow().document, 'mousemove');
    const keyPress$ = fromEvent<KeyboardEvent>(getWindow().document, 'keyup');
    const mergeBothEvents = merge(keyPress$, mouseMove$);
    const interval$ = timer(60 * 60 * 1000); /// 1 hour
    
    mergeBothEvents.pipe(
      startWith('initial'),
      switchMap(() => interval$)
    ).subscribe(() => {
      location.reload();
    });
  }
}
